import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Loader2, Megaphone } from "lucide-react";
import { toast } from "sonner";

export default function NewAnnouncementPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "",
    body: "",
    audience: "all",
    priority: "info",
    expires_at: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const onChange = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.title.trim() || !form.body.trim()) {
      setErr("Título y mensaje son obligatorios.");
      return;
    }
    setSubmitting(true);
    try {
      const body = { ...form, expires_at: form.expires_at || null };
      const { data } = await api.post("/announcements", body);
      toast.success("Comunicado publicado");
      navigate(`/announcements/${data.id}`);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-8 max-w-3xl mx-auto" data-testid="new-announcement-page">
      <button
        onClick={() => navigate(-1)}
        className="text-xs uppercase tracking-widest text-zinc-500 hover:text-black flex items-center gap-2 mb-6"
      >
        <ArrowLeft size={14} /> Volver
      </button>
      <div className="mb-6 sm:mb-8 flex items-center gap-3">
        <Megaphone size={28} />
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
            Comunicados
          </div>
          <h1 className="font-display text-3xl sm:text-5xl font-bold tracking-tight">
            Nuevo comunicado
          </h1>
        </div>
      </div>

      <form onSubmit={submit} className="border border-black bg-white" data-testid="new-announcement-form">
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest">Título *</Label>
            <Input
              value={form.title}
              onChange={onChange("title")}
              required
              className="mt-2 h-11 rounded-none border-black"
              placeholder="Ej. Cambio de horario de operaciones"
              data-testid="ann-title-input"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest">Mensaje *</Label>
            <Textarea
              value={form.body}
              onChange={onChange("body")}
              required
              rows={9}
              className="mt-2 rounded-none border-black min-h-[200px]"
              placeholder="Escribe aquí el comunicado. Puedes usar saltos de línea para estructurarlo."
              data-testid="ann-body-input"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Destinatario</Label>
            <Select value={form.audience} onValueChange={(v) => setForm((p) => ({ ...p, audience: v }))}>
              <SelectTrigger className="mt-2 h-11 rounded-none border-black" data-testid="ann-audience-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all">Toda la organización</SelectItem>
                <SelectItem value="drivers">Solo conductores</SelectItem>
                <SelectItem value="staff">Solo personal interno</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Prioridad</Label>
            <Select value={form.priority} onValueChange={(v) => setForm((p) => ({ ...p, priority: v }))}>
              <SelectTrigger className="mt-2 h-11 rounded-none border-black" data-testid="ann-priority-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="info">Informativo</SelectItem>
                <SelectItem value="important">Importante</SelectItem>
                <SelectItem value="urgent">Urgente</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest">Vigente hasta (opcional)</Label>
            <Input
              type="date"
              value={form.expires_at}
              onChange={onChange("expires_at")}
              className="mt-2 h-11 rounded-none border-black"
            />
          </div>

          {err && (
            <div className="md:col-span-2 text-sm text-red-700 bg-red-50 border border-red-300 px-3 py-2">
              {err}
            </div>
          )}
        </div>
        <div className="border-t border-black p-4 flex justify-end gap-3 bg-zinc-50">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
            className="rounded-none border-black h-11 px-5 text-xs uppercase tracking-widest"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={submitting}
            className="rounded-none btn-primary h-11 px-6 text-xs uppercase tracking-widest"
            data-testid="publish-announcement-btn"
          >
            {submitting ? <Loader2 className="animate-spin" size={16} /> : "Publicar"}
          </Button>
        </div>
      </form>
    </div>
  );
}
